import React, { useEffect, useState } from "react";
import {
  Typography,
  TextField,
  IconButton,
  Box,
  Button,
  Container,
  Breadcrumbs,
  Link as MUILink,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Card,
  CardActions,
  CircularProgress,
  CardContent,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import { message, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  getBatch,
  addBatch,
  deleteBatch,
} from "../../redux/actions/admin/batchAction";
import PageTitle from "../../Components/PageTitle";
import EditBatch from "./EditBatch";
import axios from "../../axiosInstance";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function TabPanel(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const Batch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { batches } = useSelector((state) => state.batches);
  const [value, setValue] = useState(0);
  const [search, setSearch] = useState("");
  const [filteredBatch, setFilteredBatch] = useState([]);
  const [name, setName] = useState("");
  const [duration, setDuration] = useState("");
  const [mode, setMode] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(startDate);
  const [studentCount, setStudentCount] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  useEffect(() => {
    dispatch(getBatch());
  }, [dispatch]);

  useEffect(() => {
    setFilteredBatch(batches);
  }, [batches]);

  // Total batch student count
  useEffect(() => {
    const fetchStudentCounts = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          "/api/v1/student/admin-batch-student-count"
        );
        if (response.data.success) {
          setStudentCount(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching student counts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudentCounts();
  }, []);

  // Function to refresh the Events & News data
  const refreshData = () => {
    dispatch(getBatch()); // Refresh data after update
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  // Filter teachers based on search query
  const filteredNewBatch = filteredBatch?.filter((batch) =>
    batch?.name?.toLowerCase().includes(search?.toLowerCase())
  );

  // Handle Delete Api Batch & Class
  const { confirm } = Modal;
  const handleDeleteBatch = async (e, id) => {
    e.preventDefault();
    confirm({
      title: "Are you sure you want to delete this Batch & Class?",
      content: "Once deleted, this course cannot be recovered.",
      okText: "Yes, Delete",
      cancelText: "No, Cancel",
      onOk: async () => {
        await dispatch(deleteBatch(id));
        await dispatch(getBatch());
      },
    });
  };

  // Enroll Student Batch
  const enrollStudentBatch = (e, batchId, name) => {
    e.preventDefault();
    confirm({
      title: `Are you sure you want to enroll in Batch "${name}"?`,
      content: "You will be enrolled in the selected batch once you confirm.",
      okText: "Yes, Enroll",
      cancelText: "No, Cancel",
      onOk: () => {
        navigate(`/admin/enroll/student`, {
          state: { batchId, name },
        });
        window.location.reload("/admin/enroll/student");
      },
      onCancel() {
        console.log("Enrollment canceled");
      },
    });
  };

  const batchStartDate = moment(startDate).format("MM/DD/YYYY");
  const batchEndDate = moment(endDate).format("MM/DD/YYYY");

  // Handle Api Course
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name) {
      message.error("Please enter a batch & class name");
      return;
    }
    if (!duration) {
      message.error("Please enter a batch & class duration");
      return;
    }
    if (!mode) {
      message.error("Please select a batch & class mode");
      return;
    }
    if (!startDate) {
      message.error("Please select a batch & class start date");
      return;
    }
    if (!endDate) {
      message.error("Please select a batch & class end date");
      return;
    }
    const batchData = {
      name,
      duration,
      mode,
      startDate: batchStartDate,
      endDate: batchEndDate,
    };
    await dispatch(addBatch(batchData));
    await dispatch(getBatch());
    setName("");
    setDuration("");
    setMode("");
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <>
      <PageTitle
        title="Manage Batches - Admin Panel | Shree Mangalam School"
        description="Organize and manage student batches efficiently with the Admin Panel of Shree Mangalam School. Assign teachers, schedule classes, and track batch progress."
        url="/admin/batch"
        keywords="admin batch management, manage school batches, Shree Manglam batch panel, student group admin, class scheduling, teacher assignment"
      />

      <Typography
        variant="h5"
        align="center"
        gutterBottom
        sx={{
          mb: 3,
          fontFamily: "Poppins, sans-serif",
          color: "#2C3E50",
          letterSpacing: "2.5px",
          lineHeight: 1.8,
        }}
      >
        Batch & Class
      </Typography>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          mb: 5,
          backgroundColor: "#fff",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <MUILink
          component={Link}
          to="/dashboard"
          sx={{ color: "inherit", textDecoration: "none" }}
        >
          Dashboard
        </MUILink>
        <Typography sx={{ color: "primary.main" }}>Batch & Class</Typography>
      </Breadcrumbs>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Batch & Class List" {...TabPanel(0)} />
          <Tab label="Add Batch & Class" {...TabPanel(1)} />
        </Tabs>
      </Box>
      {/* Batch & Class List */}
      <CustomTabPanel value={value} index={0}>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Batch & Class List
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by Batch & Class Name"
          value={search}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <IconButton>
                <SearchIcon sx={{ color: "#1976d2" }} />
              </IconButton>
            ),
          }}
          sx={{
            mb: 3,
            mt: 2,
            bgcolor: "white",
            borderRadius: "20px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                border: "none",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
          }}
        />
        <Grid container spacing={3} sx={{ pb: 2, pt: 2 }}>
          {filteredNewBatch.map((batch, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card
                sx={{
                  transition: "0.3s !important",
                  "&:hover": { transform: "scale(1.05)" },
                  borderRadius: "15px",
                  boxShadow:
                    "0 4px 8px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.1)",
                  // padding: "16px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  background: "#f0f0ff",
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    className="courses_title"
                    sx={{
                      letterSpacing: ".1rem",
                      fontSize: { xs: "18px", sm: "20px" },
                      marginBottom: "12px",
                      fontWeight: "bold",
                      fontFamily: "Poppins, sans-serif",
                      color: "#2C3E50",
                      // color: "#1E88E5",
                    }}
                  >
                    {batch.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="courses_desc"
                    sx={{
                      mt: 1,
                      fontSize: "15px",
                      letterSpacing: ".1rem",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    <strong style={{ color: "#000" }}>Mode</strong>:{" "}
                    {batch.mode}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="courses_desc"
                    sx={{
                      mt: 1,
                      fontSize: "15px",
                      letterSpacing: ".1rem",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    <strong style={{ color: "#000" }}>Duration</strong>:{" "}
                    {batch.duration} Months
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="courses_desc"
                    sx={{
                      mt: 1,
                      fontSize: "15px",
                      letterSpacing: ".1rem",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    <strong style={{ color: "#000" }}>Start Date</strong>:{" "}
                    {batch.startDate}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="courses_desc"
                    sx={{
                      mt: 1,
                      fontSize: "15px",
                      letterSpacing: ".1rem",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    <strong style={{ color: "#000" }}>End Date</strong>:{" "}
                    {batch.endDate}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="batchTotalStudent"
                    sx={{
                      mt: 1,
                      fontSize: "15px",
                      letterSpacing: ".1rem",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    <strong style={{ color: "#000" }}> Total Student: </strong>
                    {loading ? (
                      <CircularProgress size={20} />
                    ) : (
                      studentCount[batch._id] || 0
                    )}{" "}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                    {" "}
                    <Button
                      variant="outlined"
                      className="courses_desc"
                      color="error"
                      sx={{
                        mt: 1,
                        borderRadius: "50px",
                        textTransform: "none",
                        fontFamily: "Poppins, sans-serif",
                        letterSpacing: ".1rem",
                      }}
                      onClick={(e) => handleDeleteBatch(e, batch._id)}
                    >
                      Delete
                    </Button>
                    <EditBatch
                      id={batch._id}
                      name={batch.name}
                      duration={batch.duration}
                      mode={batch.mode}
                      startDate={batch.startDate}
                      endDate={batch.endDate}
                      refreshData={refreshData}
                    />
                  </Box>
                </CardContent>
                <CardActions sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    className="courses_desc"
                    fullWidth
                    sx={{
                      mt: 1,
                      borderRadius: "50px",
                      textTransform: "none",
                      fontFamily: "Poppins, sans-serif",
                      letterSpacing: ".1rem",
                    }}
                    onClick={(e) =>
                      enrollStudentBatch(e, batch._id, batch.name)
                    }
                  >
                    Enroll Student
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </CustomTabPanel>

      {/* Create a New Add Batch & Class*/}
      <CustomTabPanel value={value} index={1} maxWidth>
        <Typography
          variant="h6"
          sx={{
            pb: 2,
            pt: 3,
            fontFamily: "Poppins, sans-serif",
            color: "#555",
            letterSpacing: "2.5px",
            lineHeight: 1.8,
          }}
        >
          Create a new Batch & Class
        </Typography>
        <Container
          onSubmit={handleSubmit}
          component="form"
          className="form_style border-style"
          maxWidth
        >
          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="batchName"
            label="Batch & Class Name *"
            name="batchName"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Batch & Class Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            sx={{
              mb: 3,
              "& .MuiInputBase-root": {
                color: "text.secondary",
              },
              "& input[type='number']": {
                MozAppearance: "textfield",
                "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                  display: "none",
                },
              },
              fieldset: { borderColor: "rgb(231, 235, 240)" },
            }}
            fullWidth
            id="batchDuration"
            label="Batch & Class Duration (Months) *"
            name="batchDuration"
            type="number"
            InputLabelProps={{
              shrink: true,
            }}
            placeholder="Batch & Class Duration (Months)"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />

          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="batch-class-mode-label">
                Batch & Class Mode
              </InputLabel>
              <Select
                labelId="batch-class-mode-label"
                id="batch-class-mode"
                label="Batch & Class Mode *"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
              >
                <MenuItem disabled>Select Batch & Class Mode</MenuItem>
                <MenuItem value="Offline">Offline</MenuItem>
                <MenuItem value="Online">Online</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box className="Date">
            <Box sx={{ mt: 3 }}>
              <h4>Batch & Class Start Date *</h4>
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                isClearable
                placeholderText="Batch & Class Start Date"
                selected={startDate}
                onChange={handleStartDateChange}
                minDate={new Date()}
                maxDate={endDate}
                value={startDate}
              />
            </Box>
            <Box sx={{ mt: 3, mb: 3 }}>
              <h4>Batch & Class End Date *</h4>
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                isClearable
                placeholderText="Batch & Class End Date"
                selected={endDate}
                onChange={handleEndDateChange}
                minDate={(new Date() && startDate) || new Date()}
                value={endDate}
                maxDate={endDate}
              />
            </Box>
          </Box>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            className="courses_desc"
            sx={{
              mt: 3,
              borderRadius: "5px",
              textTransform: "none",
              fontFamily: "Poppins, sans-serif",
              letterSpacing: ".1rem",
            }}
          >
            Add Batch & Class
          </Button>
        </Container>
      </CustomTabPanel>
    </>
  );
};

export default Batch;
